import { h, Component } from 'preact';
import util from 'preact-util';
import { observer } from 'mobx-preact';
import { Text } from 'preact-i18n';

import { isConventionMember, isConventionOrganizer } from '../../lib/conventionResults';

/**
 * Officials-only AI target-photo analysis: trigger button + verdict panel.
 * Shared by the target-photo VIEW drawer (viewConventionResultImage) and the
 * EDIT drawer (editConventionResultImage) so the feature is reachable both
 * while a stage is running (camera icon → edit drawer) and afterwards
 * (score cell → view drawer). The verdict is advisory display data written
 * server-side onto conventionResult.aiAnalysis — it never touches scores.
 *
 * Props: stores, id (conventionResult id).
 */
@observer
class ConventionResultAiAnalysisPanel extends Component {
    constructor(props) {
        super(props);
        this.state = {};
    }

    analyzeTargets = async () => {
        const { id } = this.props;
        const { conventionResultStore } = this.props.stores;
        this.setState({ analyzing: true, analyzeError: null });
        try {
            const response = await util.fetchApi(`/api/conventionresults/${id}/analyze-target`, { method: 'POST' }, {});
            if (response && response.status === 200) {
                // Refresh the row so the stored verdict (and anything else the
                // server touched) is what we display.
                await conventionResultStore.load(id, false, { query: {} });
            } else {
                this.setState({ analyzeError: (response && response.message) || 'Analysis failed' });
            }
        } catch (err) {
            this.setState({ analyzeError: (err && err.message) || 'Analysis failed' });
        }
        this.setState({ analyzing: false });
    }

    render() {
        const { userStore, conventionResultStore, conventionStore } = this.props.stores;
        const { user = {}, isAdmin, isCustomerAdmin } = userStore;
        const { conventionResult } = conventionResultStore;
        const { analyzing, analyzeError } = this.state;
        const { aiAnalysis, images } = conventionResult || {};

        // Officials only in v1 — the backend enforces the same gate.
        const { convention } = conventionStore || {};
        const isOfficial = isAdmin || isCustomerAdmin
            || (!!convention && !!user.id && convention.user === user.id)
            || isConventionMember(convention, user)
            || isConventionOrganizer(convention, user);
        if (!isOfficial) {
            return null;
        }
        const hasPhotos = Array.isArray(images) && images.length > 0;

        return (
            <div class='mx-3 mt-3'>
                {aiAnalysis && aiAnalysis.status === 'done' && (
                    <div class={`alert py-2 mb-2 ${aiAnalysis.matchesEntered === true ? 'alert-success' : (aiAnalysis.matchesEntered === false ? 'alert-warning' : 'alert-secondary')}`}>
                        <div class='d-flex align-items-center' style='font-weight: 600;'>
                            <i class={`me-2 fa-duotone ${aiAnalysis.matchesEntered === true ? 'fa-circle-check' : (aiAnalysis.matchesEntered === false ? 'fa-triangle-exclamation' : 'fa-circle-question')}`} />
                            {aiAnalysis.matchesEntered === true && <Text id='conventionResult.ai-matches'>Matches the entered score</Text>}
                            {aiAnalysis.matchesEntered === false && <Text id='conventionResult.ai-mismatch'>Differs from the entered score</Text>}
                            {aiAnalysis.matchesEntered !== true && aiAnalysis.matchesEntered !== false && <Text id='conventionResult.ai-uncertain'>Uncertain count</Text>}
                        </div>
                        <div class='mt-1' style='font-size: 0.9rem;'>
                            {aiAnalysis.hits !== null && aiAnalysis.hits !== undefined && <span class='me-3'>Treff: <strong>{aiAnalysis.hits}</strong></span>}
                            {aiAnalysis.figures !== null && aiAnalysis.figures !== undefined && <span class='me-3'>Skvr: <strong>{aiAnalysis.figures}</strong></span>}
                            {aiAnalysis.inner !== null && aiAnalysis.inner !== undefined && <span class='me-3'>iX: <strong>{aiAnalysis.inner}</strong></span>}
                            {aiAnalysis.confidence !== null && aiAnalysis.confidence !== undefined && (
                                <span class='text-body-secondary'>
                                    <Text id='conventionResult.ai-confidence'>Confidence</Text>: {aiAnalysis.confidence}%
                                </span>
                            )}
                        </div>
                        {aiAnalysis.notes && <div class='mt-1 text-body-secondary' style='font-size: 0.85rem;'>{aiAnalysis.notes}</div>}
                        <div class='mt-1 text-body-secondary' style='font-size: 0.7rem;'>
                            {aiAnalysis.model} · {util.isoDate(aiAnalysis.analyzedDate)}
                        </div>
                    </div>
                )}
                {analyzeError && (
                    <div class='alert alert-danger py-2 mb-2' style='font-size: 0.85rem;'>
                        <Text id='conventionResult.ai-failed'>Analysis failed</Text>: {analyzeError}
                    </div>
                )}
                {hasPhotos && (
                    <button
                        type='button'
                        class='btn btn-outline-primary rounded-pill'
                        onClick={this.analyzeTargets}
                        disabled={analyzing}
                    >
                        {analyzing ? <>
                            <i class='fa-duotone fa-spinner fa-spin me-2' />
                            <Text id='conventionResult.ai-analyzing'>Analyzing targets…</Text>
                        </> : <>
                            <i class='fa-duotone fa-sparkles me-2' />
                            <Text id='conventionResult.analyze-targets'>Analyze targets (AI)</Text>
                        </>}
                    </button>
                )}
            </div>
        );
    }
}

export default ConventionResultAiAnalysisPanel;
